import React, { FC, Fragment } from 'react';
import { Box, Alert, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { isSupplier } from 'utils/client-settings';
import { InvoicingState, Order, OrderRejectStatus, SubOrderWarning, SubOrderWarningKind } from 'models/order';
import { useNavigate } from 'react-router-dom';
import { permissions } from 'utils/permission';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { currentOrgVar } from 'apollo/reactive-variables/organization';

type OrderDetailsTopMessagesProps = {
    order: Order;
};
const OrderDetailsTopMessages: FC<OrderDetailsTopMessagesProps> = ({ order }: OrderDetailsTopMessagesProps) => {
    const { t: translate } = useTranslation(['orders']);
    const navigate = useNavigate();
    const currentUser = currentUserVar();
    const currentOrganization = currentOrgVar();

    const isWarranty = () => {
        return order.isWarrantyOrder && order.orderRef;
    };

    const isRelatedOrder = () => {
        return order.isCopyOrder && order.orderRef;
    };

    const modifyLocked = () => {
        return order.isProductionFinished || order.invoicingState !== InvoicingState.NOT_READY;
    };

    const labOrderModifyLocked = () => {
        return (
            permissions.isDentist(currentUser?.type) &&
            order.isCreatedByLab &&
            !order.isProductionFinished &&
            order.invoicingState === 'NOT_READY'
        );
    };
    const deviationsIndicator = () => {
        return (
            order.isOrderDeviations &&
            permissions.isLabTechnician(currentUser?.type) &&
            !permissions.isExternalTechnician(currentUser?.role)
        );
    };

    const orderCancelIndicator = () => {
        return order.rejectStatus?.toUpperCase() === OrderRejectStatus.CANCELLED;
    };

    const orderDeclineIndicator = () => {
        return order.rejectStatus?.toUpperCase() === OrderRejectStatus.DECLINED;
    };

    const navigateToOrderDetails = (orderId: string) => {
        navigate(`/order-details/${orderId}/main-view`);
    };

    // DD-3482 Marketplace: warnings about this order's sub-orders. The backend resolves
    // them only for the lab that created the sub-orders; the clinic always gets none.
    const subOrderWarningsOf = (kind: SubOrderWarningKind) =>
        (order.subOrderWarnings ?? []).filter((warning) => warning.kind === kind);

    const subOrderLinks = (warnings: SubOrderWarning[]) =>
        warnings.map((warning, index) => (
            <span key={warning.subOrder._id}>
                {index ? ', ' : ' '}
                <Link
                    sx={{ cursor: 'pointer', color: 'primary.main' }}
                    onClick={() => navigateToOrderDetails(warning.subOrder._id)}
                >
                    {warning.subOrder.orderId}
                </Link>
                {warning.subOrder.supplier?.name ? ` (${warning.subOrder.supplier.name})` : undefined}
            </span>
        ));

    const subOrderWarningBanners = [
        SubOrderWarningKind.SUB_ORDER_STILL_ACTIVE,
        SubOrderWarningKind.SUB_ORDER_DECLINED,
        SubOrderWarningKind.SUB_ORDER_CANCELLED,
    ]
        .map((kind) => ({ kind, warnings: subOrderWarningsOf(kind) }))
        .filter(({ warnings }) => warnings.length);

    const customerInactiveIndicator = () => {
        return (
            isSupplier(currentOrganization._id, order.supplier._id) &&
            (!order.customer?.active || !order.isClientEnabled)
        );
    };

    return (
        <Box>
            {customerInactiveIndicator() ? (
                <Alert severity="warning" sx={{ marginBottom: 2 }}>
                    {translate('orders:CUSTOMER_INACTIVE_WARNING')}
                </Alert>
            ) : undefined}
            {order.isImportedOrder ? (
                <Alert severity="warning" sx={{ marginBottom: 2 }}>
                    {translate('orders:IMPORTED_ORDER_INFO')}
                </Alert>
            ) : undefined}
            {isRelatedOrder() ? (
                <Alert severity="info" sx={{ marginBottom: 2 }}>
                    {translate('orders:RELATED_ORDER_FOR')}{' '}
                    <Link
                        sx={{ cursor: 'pointer', color: 'primary.main' }}
                        onClick={() => navigateToOrderDetails(order.orderRef?.order as string)}
                    >
                        {order.orderRef?.orderId}
                    </Link>
                </Alert>
            ) : undefined}
            {isWarranty() ? (
                <Alert severity="info" sx={{ marginBottom: 2 }}>
                    {translate('orders:WARRANTY_ORDER_FOR')}{' '}
                    <Link
                        sx={{ cursor: 'pointer', color: 'primary.main' }}
                        onClick={() => navigateToOrderDetails(order.orderRef?.order as string)}
                    >
                        {order.orderRef?.orderId}
                    </Link>
                </Alert>
            ) : undefined}
            {/* DD-3674: parentOrder resolves only for the lab that created the sub-order and
                subOrders only for that lab on the parent, so the subcontractor sees neither. */}
            {order.parentOrder ? (
                <Alert severity="info" sx={{ marginBottom: 2 }}>
                    {translate('orders:SUB_ORDER_FOR')}{' '}
                    <Link
                        sx={{ cursor: 'pointer', color: 'primary.main' }}
                        onClick={() => navigateToOrderDetails(order.parentOrder?._id as string)}
                    >
                        {order.parentOrder.orderId}
                    </Link>
                </Alert>
            ) : undefined}
            {order.subOrders?.length ? (
                <Alert severity="info" sx={{ marginBottom: 2 }}>
                    {translate('orders:HAS_SUB_ORDERS')}{' '}
                    {order.subOrders.map((subOrder, index) => (
                        <Fragment key={subOrder._id}>
                            {index > 0 ? ', ' : undefined}
                            <Link
                                sx={{ cursor: 'pointer', color: 'primary.main' }}
                                onClick={() => navigateToOrderDetails(subOrder._id)}
                            >
                                {subOrder.orderId}
                            </Link>
                        </Fragment>
                    ))}
                </Alert>
            ) : undefined}
            {labOrderModifyLocked() ? (
                <Alert severity="info" sx={{ marginBottom: 2 }}>
                    {translate('orders:LAB_ORDER_INFO')}
                </Alert>
            ) : undefined}
            {modifyLocked() ? (
                <Alert severity="info" sx={{ marginBottom: 2 }}>
                    {translate('orders:MODIFY_ORDER_INFO')}
                </Alert>
            ) : undefined}
            {deviationsIndicator() ? (
                <Alert severity="warning" sx={{ marginBottom: 2 }}>
                    {translate('orders:THIS_ORDER_HAS_DEVIATIONS')}
                </Alert>
            ) : undefined}
            {orderCancelIndicator() ? (
                <Alert severity="warning" sx={{ marginBottom: 2 }}>
                    {translate('orders:THIS_ORDER_HAS_CANCELLED')}
                    {order.cancel?.message ? `: ${order.cancel.message}` : undefined}
                </Alert>
            ) : undefined}
            {orderDeclineIndicator() ? (
                <Alert severity="warning" sx={{ marginBottom: 2 }}>
                    {translate('orders:THIS_ORDER_HAS_DECLINED')}
                    {order.decline && order.decline.length > 0 ? `: ${order.decline[0]?.message}` : undefined}
                </Alert>
            ) : undefined}
            {subOrderWarningBanners.map(({ kind, warnings }) => (
                <Alert
                    key={kind}
                    severity="warning"
                    sx={{ marginBottom: 2 }}
                    data-test={`order-details-top-messages.${kind.toLowerCase()}`}
                >
                    {translate(`orders:${kind}_WARNING`)}
                    {subOrderLinks(warnings)}
                </Alert>
            ))}
            {order.deliveryDetails.destination._id !== order.deliveryDetails.pickUpLocation._id ? (
                <Alert severity="warning" sx={{ marginBottom: 2 }}>
                    {translate('orders:DELIVERY_WARNING')}
                </Alert>
            ) : undefined}
        </Box>
    );
};

export default OrderDetailsTopMessages;
